import * as yup from 'yup';
import { IconButton, InputAdornment } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import SearchIcon from '@mui/icons-material/Search';

import {
  AccessMessage,
  ContainerFileAndConsult,
  EmailTextFiled,
  Form,
  FileAndConsult,
  Link,
  LoginButton,
  LoginFormContainer,
  LoginImage,
  LoginStyled,
  MainTitle,
  PasswordTextFiled,
  SecondaryTitle
} from './Login.styles';

import { useCallback, useContext, useState } from 'react';
import IFormError from '../../interfaces/IFormError';
import { useValidateSchema } from '../../hooks/providers/useValidateSchema';
import { useLoginService } from '../../hooks/services/useLoginService';
import ILogin from '../../interfaces/ILogin';
import UserContext from '../../context/user/UserContext';
import { useSessionStorage } from '../../hooks/providers/useSessionStorage';
import { useToastrVanila } from '../../hooks/util/useToastrVanila';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import IPage from '../../interfaces/IPage';
import { useNavigate } from 'react-router-dom';
import ThemeOptionContext from '../../context/theme-option/ThemeOptionContext';
import debounce from 'lodash.debounce';
import { useSystemInformationService } from '../../hooks/services/useSystemInformationService';
import moment from 'moment';

const Login = (props: IPage) => {
  window.document.title = props.title;
  const toastr = useToastrVanila();
  const [errors, setErrors] = useState<IFormError | null>(null);
  const { setState: setUserState } = useContext(UserContext);
  const sessionStorage = useSessionStorage();
  const [loading, setLoading] = useState(false);
  const validation = useValidateSchema();
  const useLogin = useLoginService();
  const [showPassword, setShowPassword] = useState(false);
  const { state: themeState } = useContext(ThemeOptionContext);
  const request = useSystemInformationService();

  const navigate = useNavigate();

  const schema = yup.object({
    username: yup
      .string()
      .required('E-mail não informado')
      .matches(/.+@.+\..+/, 'Informe um e-mail válido'),
    password: yup.string().required('Senha não informada'),
  });
  const [login, setLogin] = useState<ILogin>({ username: '', password: '' });

  const useTimeClocking = async (tenantDomain: string) => {
    await request
      .getUseTimeClockingTenant(tenantDomain)
      .then(async result => {
        localStorage.setItem('useTimeClocking', String(result.useTimeClocking));
        localStorage.setItem('allowsAdjustTimeClock', String(result.allowsAdjustTimeClock));
      })
      .catch(error => {
        toastr.error(error.message);
      });
  };

  const listDebounce = useCallback(debounce(useTimeClocking), []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const result = await validation.validate(schema, login).then(result => {
      return result;
    });
    if (!result.isValid) {
      setErrors(result.errors);
      return;
    } else {
      setErrors(null);
    }

    setLoading(true);

    await useLogin
      .login(login)
      .then(user => {
        user.logged = true;
        setLoading(false);
        listDebounce(user.tenantDomain);
        sessionStorage.setItem('user', user);
        setUserState({ ...user, typeAuth: 'normal' });
        navigate('/home');
      })
      .catch(error => {
        setLoading(false);
        toastr.error(error.message);
      });
  };

  return (
    <LoginStyled isDark={themeState.isDark}>
      <LoginImage />
      <LoginFormContainer>
        <MainTitle>Senior Norte PR</MainTitle>
        <SecondaryTitle>Atestado Digital</SecondaryTitle>
        <Form onSubmit={e => handleSubmit(e)}>
          <AccessMessage>Acesse sua conta</AccessMessage>
          <EmailTextFiled
            variant="outlined"
            value={login.username}
            onChange={e => setLogin({ ...login, username: e.target.value })}
            label="E-mail"
            size="small"
            placeholder="usuario@dominio.com.br"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon />
                </InputAdornment>
              ),
            }}
            error={Boolean(errors?.username)}
            helperText={Boolean(errors?.username) ? errors?.username : ''}
          />
          <PasswordTextFiled
            variant="outlined"
            value={login.password}
            onChange={e => setLogin({ ...login, password: e.target.value })}
            label="Senha"
            size="small"
            type={showPassword ? 'text' : 'password'}
            placeholder="Senha"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    edge="end"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {!showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            error={Boolean(errors?.password)}
            helperText={Boolean(errors?.password) ? errors?.password : ''}
          />

          <LoginButton type="submit" variant="contained" loading={loading} loadingPosition="start" startIcon={<i />}>
            Autenticar
          </LoginButton>
        </Form>

        <ContainerFileAndConsult>
          <FileAndConsult onClick={() => {
            const medicalCertificateLocal = JSON.parse(String(localStorage.getItem('medicalCertificate'))) || null;
            const step = JSON.parse(String(localStorage.getItem('step'))) || null;

            if (
              (medicalCertificateLocal &&
                moment(new Date()).diff(moment(medicalCertificateLocal?.createdAt), 'hours') >= 1) ||
              step?.step === 15
            ) {
              localStorage.removeItem('medicalCertificate');
            }

            navigate('/');
          }}>
            <DriveFolderUploadIcon />
            <Link
            >
              Enviar Documento
            </Link>
          </FileAndConsult>
          <FileAndConsult
            onClick={() => {
              const medicalCertificateLocal = JSON.parse(String(localStorage.getItem('medicalCertificate'))) || null;
              if (
                (medicalCertificateLocal &&
                  moment(new Date()).diff(moment(medicalCertificateLocal.createdAt), 'hours') >= 1,
                  medicalCertificateLocal?.isDraft === false)
              ) {
                localStorage.removeItem('medicalCertificate');
              }
              if (localStorage.getItem('localSettings')) {
                const filterLocal = JSON.parse(String(localStorage.getItem('localSettings')));
                const TenantDomain = filterLocal.tenantDomain;
                navigate(`/consulta?tenantDomain=${TenantDomain}`);
              } else {
                navigate(`/consulta?`);
              }
            }}>
            <SearchIcon />
            <Link
            >
              Consultar Documento
            </Link>
          </FileAndConsult>
        </ContainerFileAndConsult>

      </LoginFormContainer>
    </LoginStyled>
  );
};

export default Login;
